export type RoomKind = 'person-computer' | 'person-person' | 'computer-computer';

export type ControlPreset = 'solo' | 'shared' | 'swapped';

export type Driver = 'human' | 'bot';

export type KeySide = 'bottom' | 'top';

export type MoveDir = 'up' | 'down' | 'left' | 'right';

/** Physical KeyboardEvent.code values for one player. The first code is the one shown. */
export interface KeySet {
  up: string[];
  down: string[];
  left: string[];
  right: string[];
}

export interface Settings {
  /** Master loudness, 0–1. */
  volume: number;
  muted: boolean;
  /** Background track during a match. Mute silences it too. */
  music: boolean;
  stickEnabled: boolean;
  /** Faint cross overlay on armed bombs. Live explosions still draw. */
  blastPreview: boolean;
  controls: ControlPreset;
  /** Keys for the ball at the bottom of the screen, before a swap. */
  bottomKeys: KeySet;
  /** Keys for the ball at the top of the screen, before a swap. */
  topKeys: KeySet;
}

export interface ControlPlan {
  turquoiseDriver: Driver;
  orangeDriver: Driver;
  turquoiseKeys: KeySet;
  orangeKeys: KeySet;
  stickCamp: 'TURQUOISE' | 'ORANGE';
  stickEnabled: boolean;
}

export const ROOM_LABEL: Record<RoomKind, string> = {
  'person-computer': 'Single play',
  'person-person': 'Multiple play',
  'computer-computer': 'Watch',
};

export const MOVE_DIRS: readonly MoveDir[] = ['up', 'down', 'left', 'right'];

/** WASD, plus Z and Q so an AZERTY keyboard works before anyone rebinds. */
export const DEFAULT_BOTTOM_KEYS: KeySet = {
  up: ['KeyW', 'KeyZ'],
  down: ['KeyS'],
  left: ['KeyA', 'KeyQ'],
  right: ['KeyD'],
};

export const DEFAULT_TOP_KEYS: KeySet = {
  up: ['ArrowUp'],
  down: ['ArrowDown'],
  left: ['ArrowLeft'],
  right: ['ArrowRight'],
};

export const EMPTY_KEYS: KeySet = { up: [], down: [], left: [], right: [] };

const STORAGE_KEY = 'orbomb.settings.v1';
const PRESETS: readonly ControlPreset[] = ['solo', 'shared', 'swapped'];
const MAX_KEYS_PER_DIR = 3;

const RESERVED_CODES = new Set([
  'Escape',
  'Tab',
  'F1',
  'F2',
  'F3',
  'F4',
  'F5',
  'F6',
  'F7',
  'F8',
  'F9',
  'F10',
  'F11',
  'F12',
]);

export const DEFAULT_SETTINGS: Settings = {
  volume: 0.8,
  muted: false,
  music: true,
  stickEnabled: true,
  blastPreview: true,
  controls: 'solo',
  bottomKeys: cloneKeySet(DEFAULT_BOTTOM_KEYS),
  topKeys: cloneKeySet(DEFAULT_TOP_KEYS),
};

export function cloneKeySet(set: KeySet): KeySet {
  return {
    up: [...set.up],
    down: [...set.down],
    left: [...set.left],
    right: [...set.right],
  };
}

export function isBindableKey(code: string): boolean {
  if (!code || code === 'Unidentified') return false;
  if (RESERVED_CODES.has(code)) return false;
  if (/^(Shift|Control|Alt|Meta|OS)(Left|Right)$/.test(code)) return false;
  return true;
}

function covers(set: KeySet, required: KeySet): boolean {
  return MOVE_DIRS.every((dir) => required[dir].every((code) => set[dir].includes(code)));
}

export function mergeKeySets(a: KeySet, b: KeySet): KeySet {
  const join = (dir: MoveDir) => [...new Set([...a[dir], ...b[dir]])];
  return { up: join('up'), down: join('down'), left: join('left'), right: join('right') };
}

function sanitizeKeyList(value: unknown, fallback: readonly string[]): string[] {
  if (!Array.isArray(value)) return [...fallback];
  const codes = value.filter(
    (code): code is string => typeof code === 'string' && isBindableKey(code),
  );
  const unique = [...new Set(codes)].slice(0, MAX_KEYS_PER_DIR);
  return unique.length > 0 ? unique : [...fallback];
}

function sanitizeKeySet(value: unknown, fallback: KeySet): KeySet {
  const raw = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  return {
    up: sanitizeKeyList(raw.up, fallback.up),
    down: sanitizeKeyList(raw.down, fallback.down),
    left: sanitizeKeyList(raw.left, fallback.left),
    right: sanitizeKeyList(raw.right, fallback.right),
  };
}

/** One physical key belongs to one direction. Bottom wins ties, then the defaults fill a hole. */
function dedupeKeySets(bottom: KeySet, top: KeySet): { bottomKeys: KeySet; topKeys: KeySet } {
  const used = new Set<string>();
  const take = (codes: readonly string[]): string[] => {
    const next: string[] = [];
    for (const code of codes) {
      if (used.has(code)) continue;
      used.add(code);
      next.push(code);
    }
    return next;
  };
  const bottomKeys: KeySet = {
    up: take(bottom.up),
    down: take(bottom.down),
    left: take(bottom.left),
    right: take(bottom.right),
  };
  const topKeys: KeySet = {
    up: take(top.up),
    down: take(top.down),
    left: take(top.left),
    right: take(top.right),
  };
  const fill = (set: KeySet, fallback: KeySet) => {
    for (const dir of MOVE_DIRS) {
      if (set[dir].length > 0) continue;
      const free = fallback[dir].find((code) => !used.has(code));
      if (!free) continue;
      set[dir] = [free];
      used.add(free);
    }
  };
  fill(bottomKeys, DEFAULT_BOTTOM_KEYS);
  fill(topKeys, DEFAULT_TOP_KEYS);
  return { bottomKeys, topKeys };
}

export function sanitizeSettings(value: unknown): Settings {
  const raw = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const volume =
    typeof raw.volume === 'number' && Number.isFinite(raw.volume)
      ? raw.volume
      : DEFAULT_SETTINGS.volume;
  const controls = PRESETS.find((preset) => preset === raw.controls) ?? DEFAULT_SETTINGS.controls;
  const keys = dedupeKeySets(
    sanitizeKeySet(raw.bottomKeys, DEFAULT_BOTTOM_KEYS),
    sanitizeKeySet(raw.topKeys, DEFAULT_TOP_KEYS),
  );
  return {
    volume: Math.min(1, Math.max(0, volume)),
    muted: raw.muted === true,
    music: raw.music !== false,
    stickEnabled: raw.stickEnabled !== false,
    blastPreview: raw.blastPreview !== false,
    controls,
    ...keys,
  };
}

export function loadSettings(): Settings {
  try {
    if (typeof localStorage === 'undefined') return sanitizeSettings(DEFAULT_SETTINGS);
    const text = localStorage.getItem(STORAGE_KEY);
    if (!text) return sanitizeSettings(DEFAULT_SETTINGS);
    return sanitizeSettings(JSON.parse(text) as unknown);
  } catch {
    return sanitizeSettings(DEFAULT_SETTINGS);
  }
}

export function saveSettings(settings: Settings): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitizeSettings(settings)));
  } catch {
    /* private mode or a full disk */
  }
}

/**
 * Puts `code` on one direction. A key stolen from the other player is replaced
 * with the keys that direction used to have, so neither side loses an axis.
 */
export function assignKey(settings: Settings, side: KeySide, dir: MoveDir, code: string): Settings {
  if (!isBindableKey(code)) return settings;
  const sets: Record<KeySide, KeySet> = {
    bottom: cloneKeySet(settings.bottomKeys),
    top: cloneKeySet(settings.topKeys),
  };
  const previous = [...sets[side][dir]];
  let stolen: { side: KeySide; dir: MoveDir } | null = null;
  for (const owner of ['bottom', 'top'] as const) {
    for (const axis of MOVE_DIRS) {
      if (!sets[owner][axis].includes(code)) continue;
      if (owner !== side || axis !== dir) stolen = { side: owner, dir: axis };
      sets[owner][axis] = sets[owner][axis].filter((held) => held !== code);
    }
  }
  sets[side][dir] = [code];
  if (stolen && sets[stolen.side][stolen.dir].length === 0) {
    const giveBack = previous.filter((held) => held !== code);
    if (giveBack.length > 0) sets[stolen.side][stolen.dir] = giveBack;
  }
  return sanitizeSettings({ ...settings, bottomKeys: sets.bottom, topKeys: sets.top });
}

export function withDefaultKeys(settings: Settings): Settings {
  return sanitizeSettings({
    ...settings,
    bottomKeys: DEFAULT_BOTTOM_KEYS,
    topKeys: DEFAULT_TOP_KEYS,
  });
}

export function movementCodes(settings: Settings): Set<string> {
  const codes = new Set<string>();
  for (const set of [settings.bottomKeys, settings.topKeys]) {
    for (const dir of MOVE_DIRS) {
      for (const code of set[dir]) codes.add(code);
    }
  }
  return codes;
}

const NAMED_CODES: Record<string, string> = {
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
  Space: 'Space',
  Enter: 'Enter',
  Backspace: 'Bksp',
  Minus: '-',
  Equal: '=',
  BracketLeft: '[',
  BracketRight: ']',
  Backslash: '\\',
  Semicolon: ';',
  Quote: "'",
  Comma: ',',
  Period: '.',
  Slash: '/',
  Backquote: '`',
};

export function formatKeyCode(code: string): string {
  const named = NAMED_CODES[code];
  if (named) return named;
  if (code.startsWith('Key') && code.length === 4) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (/^Numpad\d$/.test(code)) return `Num${code.slice(6)}`;
  return code;
}

export function formatKeyList(codes: readonly string[]): string {
  if (codes.length === 0) return '—';
  return codes.map(formatKeyCode).join(' ');
}

const LABEL_ORDER: readonly MoveDir[] = ['up', 'left', 'down', 'right'];

function withoutCodes(set: KeySet, used: ReadonlySet<string>): KeySet {
  const keep = (dir: MoveDir) => set[dir].filter((code) => !used.has(code));
  return { up: keep('up'), down: keep('down'), left: keep('left'), right: keep('right') };
}

/** Short name for room cards and the match hint. */
export function keySetLabel(set: KeySet): string {
  const used = new Set<string>();
  const parts: string[] = [];
  const take = (required: KeySet, name: string) => {
    if (!covers(set, required)) return;
    parts.push(name);
    for (const dir of MOVE_DIRS) {
      for (const code of required[dir]) used.add(code);
    }
  };
  take(DEFAULT_TOP_KEYS, 'Arrows');
  take(DEFAULT_BOTTOM_KEYS, 'WASD');
  const rest = withoutCodes(set, used);
  const hasRest = MOVE_DIRS.some((dir) => rest[dir].length > 0);
  if (!hasRest) return parts.join(', ');
  const letters = LABEL_ORDER.map((dir) => formatKeyCode(rest[dir][0] ?? '—')).join(' ');
  return parts.length > 0 ? `${parts.join(', ')}, ${letters}` : letters;
}

function presetKeys(
  settings: Settings,
  preset: ControlPreset,
): Pick<ControlPlan, 'turquoiseKeys' | 'orangeKeys' | 'stickCamp'> {
  if (preset === 'shared') {
    return {
      turquoiseKeys: settings.bottomKeys,
      orangeKeys: settings.topKeys,
      stickCamp: 'TURQUOISE',
    };
  }
  if (preset === 'swapped') {
    return {
      turquoiseKeys: settings.topKeys,
      orangeKeys: settings.bottomKeys,
      stickCamp: 'TURQUOISE',
    };
  }
  return {
    turquoiseKeys: mergeKeySets(settings.bottomKeys, settings.topKeys),
    orangeKeys: EMPTY_KEYS,
    stickCamp: 'TURQUOISE',
  };
}

/** One signed-in player on this device. Both saved key sets move them. */
export function planForOnline(settings: Settings): ControlPlan {
  return planForRoom({ ...settings, controls: 'solo' }, 'person-computer');
}

/** How a room drives each camp. Person vs person always keeps a two-player split. */
export function planForRoom(settings: Settings, room: RoomKind): ControlPlan {
  const keys = presetKeys(
    settings,
    settings.controls === 'solo' && room === 'person-person' ? 'shared' : settings.controls,
  );
  const drivers =
    room === 'computer-computer'
      ? { turquoiseDriver: 'bot' as const, orangeDriver: 'bot' as const }
      : room === 'person-computer'
        ? { turquoiseDriver: 'human' as const, orangeDriver: 'bot' as const }
        : { turquoiseDriver: 'human' as const, orangeDriver: 'human' as const };
  return { ...keys, ...drivers, stickEnabled: settings.stickEnabled };
}

/** Keys a bot camp is not using fall through to the lone human, so arrows still steer you. */
export function keysForCamp(plan: ControlPlan): { turquoise: KeySet; orange: KeySet } {
  const turquoiseHuman = plan.turquoiseDriver === 'human';
  const orangeHuman = plan.orangeDriver === 'human';
  if (turquoiseHuman && !orangeHuman) {
    return {
      turquoise: mergeKeySets(plan.turquoiseKeys, plan.orangeKeys),
      orange: EMPTY_KEYS,
    };
  }
  if (orangeHuman && !turquoiseHuman) {
    return {
      orange: mergeKeySets(plan.orangeKeys, plan.turquoiseKeys),
      turquoise: EMPTY_KEYS,
    };
  }
  return {
    turquoise: turquoiseHuman ? plan.turquoiseKeys : EMPTY_KEYS,
    orange: orangeHuman ? plan.orangeKeys : EMPTY_KEYS,
  };
}

/** Human camp that owns the left gamepad stick. The on-screen stick uses the same camp. */
export function localCamp(plan: ControlPlan): 'TURQUOISE' | 'ORANGE' | null {
  const other = plan.stickCamp === 'TURQUOISE' ? 'ORANGE' : 'TURQUOISE';
  const driver = (camp: 'TURQUOISE' | 'ORANGE') =>
    camp === 'TURQUOISE' ? plan.turquoiseDriver : plan.orangeDriver;
  if (driver(plan.stickCamp) === 'human') return plan.stickCamp;
  if (driver(other) === 'human') return other;
  return null;
}

export function stickTarget(plan: ControlPlan): 'TURQUOISE' | 'ORANGE' | null {
  if (!plan.stickEnabled) return null;
  return localCamp(plan);
}

function schemeLabel(set: KeySet, stick: boolean): string {
  const keys = keySetLabel(set);
  if (keys && stick) return `${keys} and the stick`;
  if (keys) return keys;
  if (stick) return 'the stick';
  return 'no input';
}

export function roomBlurb(settings: Settings, room: RoomKind): string {
  const plan = planForRoom(settings, room);
  const assigned = keysForCamp(plan);
  const stick = stickTarget(plan);
  if (room === 'computer-computer') {
    return 'Two computers play the match, so you can watch how it is played.';
  }
  if (room === 'person-computer') {
    return `You play at the bottom against a computer. ${schemeLabel(assigned.turquoise, stick === 'TURQUOISE')} move you.`;
  }
  return 'Create a room and wait for someone, or join an open room.';
}

export function matchHelp(settings: Settings, room: RoomKind): string {
  const title = ROOM_LABEL[room];
  if (room === 'computer-computer') return `${title} · Home leaves the room`;
  const plan = planForRoom(settings, room);
  const assigned = keysForCamp(plan);
  const stick = stickTarget(plan);
  if (room === 'person-computer') {
    return `${title} · ${schemeLabel(assigned.turquoise, stick === 'TURQUOISE')} · P pause`;
  }
  return `${title} · bottom ${schemeLabel(assigned.turquoise, stick === 'TURQUOISE')} · top ${schemeLabel(assigned.orange, stick === 'ORANGE')}`;
}

const DIR_WORD: Record<MoveDir, string> = {
  up: 'up',
  down: 'down',
  left: 'left',
  right: 'right',
};

export function bindPrompt(side: KeySide, dir: MoveDir): string {
  return `Press a key for ${side} ${DIR_WORD[dir]}. Esc cancels.`;
}
